import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchApplySettlement, fetchConfirmStatement, fetchDisputeStatement, fetchProjectStatements } from '@/service/api/revenue';
import { formatDate, formatMoney } from './utils';

interface BillingTabProps {
  project: Api.Project.ProjectDetailResponse;
  onRefresh?: () => void;
}

export default function BillingTab({ project, onRefresh }: BillingTabProps) {
  const [statements, setStatements] = useState<Api.Revenue.StatementResponse[]>(project.revenue_statements || []);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [operatingId, setOperatingId] = useState<string | null>(null);

  // 记忆上次请求的 projectId 锁，防止初始化重复触发
  const lastFetchKeyRef = useRef<string>('');

  const loadStatements = useCallback(async (force = false) => {
    if (!project?.id) return;

    const requestKey = String(project.id);
    if (!force && lastFetchKeyRef.current === requestKey) {
      return;
    }
    lastFetchKeyRef.current = requestKey;

    setLoading(true);
    setError('');
    try {
      const res = await fetchProjectStatements(project.id);
      if (res && Array.isArray(res.items)) {
        setStatements(res.items);
      } else if (Array.isArray(res)) {
        setStatements(res as unknown as Api.Revenue.StatementResponse[]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取收益账单数据失败');
      lastFetchKeyRef.current = '';
    } finally {
      setLoading(false);
    }
  }, [project?.id]);

  useEffect(() => {
    loadStatements();
  }, [loadStatements]);

  /** 确认收益账单 POST /my/statements/{id}/confirm */
  const handleConfirmStatement = async (statementId?: string) => {
    if (!statementId) return;
    if (!confirm('确认要同意该收益账单吗？确认后将可发起结算申请。')) return;

    setOperatingId(statementId);
    try {
      await fetchConfirmStatement(statementId);
      alert('账单确认成功！');
      loadStatements(true);
      onRefresh?.();
    } catch (err) {
      alert('确认账单失败: ' + (err instanceof Error ? err.message : '网络错误'));
    } finally {
      setOperatingId(null);
    }
  };

  /** 提交账单异议 POST /my/statements/{id}/dispute */
  const handleDisputeStatement = async (statementId?: string) => {
    if (!statementId) return;
    const note = prompt('请输入针对该账单的异议说明原因：');
    if (!note || !note.trim()) return;

    setOperatingId(statementId);
    try {
      await fetchDisputeStatement(statementId, { note: note.trim() });
      alert('账单异议已成功提交！');
      loadStatements(true);
      onRefresh?.();
    } catch (err) {
      alert('提交异议失败: ' + (err instanceof Error ? err.message : '网络错误'));
    } finally {
      setOperatingId(null);
    }
  };

  /** 基于已确认账单申请结算 POST /my/statements/{id}/settle */
  const handleSettleStatement = async (statementId?: string) => {
    if (!statementId) return;
    if (!confirm('确认要基于该收益账单申请结算吗？')) return;

    setOperatingId(statementId);
    try {
      await fetchApplySettlement(statementId);
      alert('结算申请已成功提交！');
      loadStatements(true);
      onRefresh?.();
    } catch (err) {
      alert('申请结算失败: ' + (err instanceof Error ? err.message : '网络错误'));
    } finally {
      setOperatingId(null);
    }
  };

  /** 获取账单状态文案与样式 (对照 statement_status 枚举说明) */
  const getStatementStatusInfo = (status?: string) => {
    switch (status) {
      case 'PENDING_ISSUE':
        return { label: '待生成账单', color: 'bg-gray-100 text-gray-700 border-gray-200' };
      case 'ISSUED':
        return { label: '账单已生成', color: 'bg-blue-100 text-blue-700 border-blue-200' };
      case 'PENDING_OWNER_CONFIRMATION':
      case 'PENDING':
      case 'PENDING_CONFIRMATION':
        return { label: '待内容方确认', color: 'bg-amber-100 text-amber-800 border-amber-200' };
      case 'OWNER_CONFIRMED':
      case 'CONFIRMED':
        return { label: '内容方已确认', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
      case 'PENDING_SETTLEMENT':
        return { label: '待结算付款', color: 'bg-purple-100 text-purple-700 border-purple-200' };
      case 'SETTLED':
        return { label: '已完成结算', color: 'bg-green-100 text-green-700 border-green-200' };
      case 'DISPUTED':
        return { label: '异议处理中', color: 'bg-red-100 text-red-700 border-red-200' };
      case 'VOID':
        return { label: '已作废', color: 'bg-gray-100 text-gray-500 border-gray-200' };
      default:
        return { label: status || '未知状态', color: 'bg-gray-100 text-gray-600 border-gray-200' };
    }
  };

  /** 格式化分成比例说明 */
  const formatRateText = (rate?: number | string) => {
    if (rate === undefined || rate === null || rate === '') return '';
    const num = Number(rate);
    if (isNaN(num)) return '';
    const val = num <= 1 ? num * 100 : num;
    return `${val.toFixed(0)}%`;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-foreground-950 text-sm">收益账单</h3>
          <p className="text-xs text-foreground-500 mt-1">每月 25 日自动生成上月收益账单，请在 5 个工作日内确认</p>
        </div>
        <button
          onClick={() => loadStatements(true)}
          disabled={loading}
          className="text-xs px-3 py-1.5 rounded-lg border border-background-300 hover:bg-gray-50 text-foreground-700 flex items-center gap-1.5 transition-colors cursor-pointer disabled:opacity-50"
        >
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          刷新数据
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-foreground-400 text-sm glass-card rounded-xl">
          <i className="ri-loader-4-line text-3xl block mb-2 text-foreground-400 animate-spin"></i>
          正在加载收益账单数据...
        </div>
      ) : error ? (
        <div className="py-10 text-center text-rose-500 text-sm glass-card rounded-xl">
          <i className="ri-error-warning-line text-3xl block mb-2 text-rose-400"></i>
          {error}
        </div>
      ) : statements.length === 0 ? (
        <div className="py-8 text-center text-foreground-400 text-sm glass-card rounded-xl">
          <i className="ri-bill-line text-2xl block mb-2 text-foreground-300"></i>
          暂无收益账单记录
        </div>
      ) : (
        <div className="space-y-3">
          {statements.map((bill) => {
            const isPendingConfirmation =
              bill.status === 'PENDING_OWNER_CONFIRMATION' ||
              bill.status === 'PENDING' ||
              bill.status === 'PENDING_CONFIRMATION' ||
              bill.status === 'ISSUED';
            const isConfirmed =
              bill.status === 'OWNER_CONFIRMED' ||
              bill.status === 'CONFIRMED';
            const isOperating = operatingId === bill.id;
            const periodText = bill.period_start_date
              ? `${formatDate(bill.period_start_date)} ~ ${formatDate(bill.period_end_date)}`
              : bill.statement_no || '月度收益账单';
            const statusInfo = getStatementStatusInfo(bill.status);

            const ownerRateText = formatRateText(bill.owner_share_rate_snapshot);
            const platformRateText = formatRateText(bill.platform_share_rate_snapshot);

            return (
              <div key={bill.id} className="p-5 rounded-xl bg-gray-50 border border-gray-200/60 transition-all hover:border-gray-300/80">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-file-text-line text-secondary-600 text-lg"></i>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-foreground-950">{periodText}</p>
                        {bill.channel_name && (
                          <span className="px-2 py-0.5 text-[11px] rounded bg-blue-50 text-blue-700 border border-blue-100 font-medium">
                            {bill.channel_name}
                          </span>
                        )}
                        {bill.revision_no && bill.revision_no > 1 && (
                          <span className="px-2 py-0.5 text-[11px] rounded bg-purple-50 text-purple-700 border border-purple-100 font-medium">
                            v{bill.revision_no} 修订版
                          </span>
                        )}
                        {bill.replaces_statement_id && (
                          <span className="px-2 py-0.5 text-[11px] rounded bg-amber-50 text-amber-700 border border-amber-100 font-medium">
                            替代旧账单
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-foreground-400 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span>账单编号：{bill.statement_no || bill.id}</span>
                        <span>生成于 {formatDate(bill.issued_at || bill.created_at)}</span>
                        {bill.owner_confirmed_at && (
                          <span className="text-emerald-700">确认于 {formatDate(bill.owner_confirmed_at)}</span>
                        )}
                        {bill.dispute_submitted_at && (
                          <span className="text-rose-700">异议于 {formatDate(bill.dispute_submitted_at)}</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 self-end sm:self-center">
                    <div className="text-right">
                      <span className="text-[11px] text-foreground-400 block">内容方应纳收益</span>
                      <p className="text-lg font-bold text-foreground-950">
                        {formatMoney(bill.owner_share_amount, bill.settlement_currency, true)}
                      </p>
                    </div>
                    {isPendingConfirmation ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleConfirmStatement(bill.id)}
                          disabled={isOperating}
                          className="px-3 py-1.5 rounded-lg bg-primary-500 text-foreground-950 text-xs font-medium hover:bg-primary-400 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                        >
                          {isOperating ? '处理中...' : '确认账单'}
                        </button>
                        <button
                          onClick={() => handleDisputeStatement(bill.id)}
                          disabled={isOperating}
                          className="px-3 py-1.5 rounded-lg border border-background-300/60 text-xs text-foreground-500 hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                        >
                          提出异议
                        </button>
                      </div>
                    ) : isConfirmed ? (
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                        <button
                          onClick={() => handleSettleStatement(bill.id)}
                          disabled={isOperating}
                          className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-500 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                        >
                          {isOperating ? '处理中...' : '申请结算'}
                        </button>
                      </div>
                    ) : (
                      <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                    )}
                  </div>
                </div>

                {/* 账单金额及分成规则明细拆解 */}
                <div className="mt-4 pt-3 border-t border-gray-200/60 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2.5 text-xs">
                  <div className="bg-white/80 p-2 rounded-lg border border-gray-200/50">
                    <span className="text-foreground-400 block text-[11px] mb-0.5">总收入 (毛收入)</span>
                    <span className="font-semibold text-foreground-800">
                      {formatMoney(bill.gross_income_amount, bill.settlement_currency)}
                    </span>
                  </div>
                  <div className="bg-white/80 p-2 rounded-lg border border-gray-200/50">
                    <span className="text-foreground-400 block text-[11px] mb-0.5">渠道扣除</span>
                    <span className="font-semibold text-foreground-800">
                      {formatMoney(bill.channel_deduction_amount, bill.settlement_currency)}
                    </span>
                  </div>
                  <div className="bg-white/80 p-2 rounded-lg border border-gray-200/50">
                    <span className="text-foreground-400 block text-[11px] mb-0.5">人工扣除</span>
                    <span className="font-semibold text-foreground-800">
                      {formatMoney(bill.manual_deduction_amount, bill.settlement_currency)}
                    </span>
                  </div>
                  <div className="bg-white/80 p-2 rounded-lg border border-gray-200/50">
                    <span className="text-foreground-400 block text-[11px] mb-0.5">可分配金额</span>
                    <span className="font-semibold text-foreground-800">
                      {formatMoney(bill.distributable_amount, bill.settlement_currency)}
                    </span>
                  </div>
                  <div className="bg-white/80 p-2 rounded-lg border border-gray-200/50">
                    <span className="text-foreground-400 block text-[11px] mb-0.5">
                      内容方分成 {ownerRateText ? `(${ownerRateText})` : ''}
                    </span>
                    <span className="font-semibold text-primary-600">
                      {formatMoney(bill.owner_share_amount, bill.settlement_currency)}
                    </span>
                  </div>
                  <div className="bg-white/80 p-2 rounded-lg border border-gray-200/50">
                    <span className="text-foreground-400 block text-[11px] mb-0.5">
                      平台分成 {platformRateText ? `(${platformRateText})` : ''}
                    </span>
                    <span className="font-semibold text-foreground-800">
                      {formatMoney(bill.platform_share_amount, bill.settlement_currency)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-6 p-4 rounded-xl bg-gray-50 border border-gray-200/60">
        <p className="text-xs text-foreground-500">
          <i className="ri-information-line text-accent-500 mr-1"></i>
          如对账单有异议，请在生成后 5 个工作日内提出，超时将默认视为确认。账单确认后可发起结算申请。
        </p>
      </div>
    </div>
  );
}


